/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, Slider, DatePicker } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Cell, ReferenceLine,
} from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY = '#1a3a5c';
const toN  = v => Number(v || 0);
const fmtE = v => `E ${toN(v).toLocaleString()}`;

function KPI({ label, value, color=NAVY, sub }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1, minWidth:120 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:20, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height=280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

const depColor = pct => pct > 80 ? '#f5222d' : pct > 50 ? '#fa8c16' : '#52c41a';

export default function FleetAnalyticsDepreciation() {
    // Uses ageing fleet report — has installation_date, cost, lifespanvalue, lifespanunit
    const [vehicles, setVehicles] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [horizon,  setHorizon]  = useState(5);
    const [region,     setRegion]    = useState('');
    const [programme,  setProgramme] = useState('');
    const [facility,   setFacility]  = useState('');
    const [vehType,    setVehType]   = useState('');
    const [dateFrom,   setDateFrom]  = useState('');
    const [dateTo,     setDateTo]    = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const r = await FleetService.reportAgeing();
            if (Number(r?.data?.code) === 200) setVehicles(r.data.result || []);
        } catch (e) { message.error('Failed to load depreciation data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const regions    = useMemo(() => [...new Set(vehicles.map(v=>v.region).filter(Boolean))], [vehicles]);
    const programmes = useMemo(() => [...new Set(vehicles.map(v=>v.assigned_programme).filter(Boolean))], [vehicles]);
    const facilities = useMemo(() => [...new Set(vehicles.map(v=>v.facility).filter(Boolean))], [vehicles]);
    const vehTypes   = useMemo(() => [...new Set(vehicles.map(v=>v.item_name||v.vehicle_type).filter(Boolean))], [vehicles]);
    const filtered = useMemo(() => {
        let d = vehicles.filter(v => toN(v.cost) > 0);
        if (region)    d = d.filter(v => v.region             === region);
        if (programme) d = d.filter(v => v.assigned_programme === programme);
        if (facility)  d = d.filter(v => v.facility           === facility);
        if (vehType)   d = d.filter(v => (v.item_name||v.vehicle_type) === vehType);
        return d;
    }, [vehicles, region]);

    // Per-vehicle straight-line depreciation
    const perVehicle = useMemo(() => filtered.map(v => {
        const cost      = toN(v.cost);
        const lifespan  = toN(v.lifespanvalue) || 10;
        const startYear = v.installation_date
            ? new Date(v.installation_date).getFullYear() : new Date().getFullYear();
        const age       = new Date().getFullYear() - startYear;
        const salvage   = cost * 0.1;
        const annualDep = lifespan > 0 ? (cost - salvage) / lifespan : 0;
        const bookVal   = Math.max(salvage, cost - annualDep * age);
        const depPct    = cost > 0 ? ((cost - bookVal) / cost * 100) : 0;
        return {
            reg:       v.registration_no, type: v.vehicle_type,
            region:    v.region,          facility: v.facility,
            cost, lifespan, age,
            bookVal:   Math.round(bookVal),
            annualDep: Math.round(annualDep),
            depPct,
            status:    depPct > 80 ? 'End of Life' : depPct > 50 ? 'Mid Life' : 'Early',
        };
    }).sort((a,b) => b.depPct - a.depPct), [filtered]);

    // Fleet-level projection curve
    const currentYear  = new Date().getFullYear();
    const projCurve    = useMemo(() => {
        const years = Array.from({length: horizon+1}, (_,i) => currentYear + i);
        return years.map(yr => {
            let bookVal = 0, dep = 0;
            perVehicle.forEach(v => {
                const age   = yr - (v.reg ? currentYear - v.age : currentYear);
                const bv    = Math.max(v.cost * 0.1, v.cost - v.annualDep * age);
                bookVal += bv;
                dep     += v.annualDep;
            });
            return { year: yr, bookValue: Math.round(bookVal), annualDep: Math.round(dep) };
        });
    }, [perVehicle, horizon, currentYear]);

    // Age buckets
    const ageBuckets = useMemo(() => {
        const m = {'0–2 yrs':0,'3–5 yrs':0,'6–8 yrs':0,'9+ yrs':0};
        perVehicle.forEach(v => {
            if (v.age<=2) m['0–2 yrs']++;
            else if (v.age<=5) m['3–5 yrs']++;
            else if (v.age<=8) m['6–8 yrs']++;
            else m['9+ yrs']++;
        });
        return Object.entries(m).map(([name,count])=>({name,count}));
    }, [perVehicle]);

    const totalProcurement = perVehicle.reduce((s,v)=>s+v.cost,0);
    const totalBook        = perVehicle.reduce((s,v)=>s+v.bookVal,0);
    const endOfLife        = perVehicle.filter(v=>v.status==='End of Life').length;

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(perVehicle.map(v=>({
            'Reg No':v.reg,'Type':v.type,'Region':v.region,'Facility':v.facility,
            'Procurement (E)':v.cost,'Age (yrs)':v.age,'Lifespan (yrs)':v.lifespan,
            'Book Value (E)':v.bookVal,'Annual Dep (E)':v.annualDep,
            'Dep %':v.depPct.toFixed(1),'Status':v.status,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb,ws,'Depreciation');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Fleet_Depreciation_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Depreciation vs Cost Analysis</h4>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Straight-line depreciation · book value tracking · fleet age profile
                    </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ fontSize:12, color:'#666' }}>Horizon:</span>
                    <div style={{ width:110 }}>
                        <Slider min={3} max={15} value={horizon} onChange={setHorizon}
                            tooltip={{ formatter:v=>`${v}yr` }}/>
                    </div>
                    <Select allowClear placeholder="Region" size="small" style={{ width:120 }}
                        value={region||undefined} onChange={v=>setRegion(v||'')}>
                        {regions.map(r=><Select.Option key={r} value={r}>{r}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Programme" size="small" style={{ width:140 }}
                        value={programme||undefined} onChange={v=>setProgramme(v||'')}>
                        {programmes.map(p=><Select.Option key={p} value={p}>{p}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Facility" size="small" style={{ width:140 }}
                        value={facility||undefined} onChange={v=>setFacility(v||'')}>
                        {facilities.map(f=><Select.Option key={f} value={f}>{f}</Select.Option>)}
                    </Select>
                    <Select allowClear placeholder="Type" size="small" style={{ width:130 }}
                        value={vehType||undefined} onChange={v=>setVehType(v||'')}>
                        {vehTypes.map(t=><Select.Option key={t} value={t}>{t}</Select.Option>)}
                    </Select>
                    <Button size="small" icon={<ReloadOutlined/>} onClick={load}/>
                    <Button size="small" type="primary" icon={<DownloadOutlined/>}
                        onClick={exportXlsx} disabled={!perVehicle.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
                    <KPI label="Vehicles Analysed"   value={filtered.length}          color={NAVY}/>
                    <KPI label="Total Procurement"   value={fmtE(totalProcurement)}   color="#722ed1"/>
                    <KPI label="Current Book Value"  value={fmtE(totalBook)}          color="#2e86c1"
                        sub={`${totalProcurement?Math.round(totalBook/totalProcurement*100):0}% retained`}/>
                    <KPI label="Total Depreciated"   value={fmtE(totalProcurement-totalBook)} color="#fa8c16"/>
                    <KPI label="End of Life (>80%)"  value={endOfLife}                color="#f5222d" sub="need replacement"/>
                </div>

                <ChartCard title={`Fleet Book Value Projection — ${horizon} years (E)`} height={250}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={projCurve} margin={{top:5,right:20,left:20,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                            <XAxis dataKey="year" tick={{fontSize:11}}/>
                            <YAxis tick={{fontSize:10}} tickFormatter={v=>`E${(v/1e6).toFixed(1)}M`}/>
                            <Tooltip formatter={(v,n)=>[`E ${toN(v).toLocaleString()}`,n]}/>
                            <Legend wrapperStyle={{fontSize:11}}/>
                            <ReferenceLine x={currentYear} stroke={NAVY} strokeDasharray="4 2"
                                label={{value:'Today',fontSize:10,fill:NAVY,position:'insideTopRight'}}/>
                            <Line type="monotone" dataKey="bookValue"  name="Book Value"         stroke={NAVY}     strokeWidth={3} dot={false}/>
                            <Line type="monotone" dataKey="annualDep"  name="Annual Depreciation" stroke="#fa8c16" strokeWidth={2} dot={false} strokeDasharray="5 3"/>
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
                    <ChartCard title="Vehicle Depreciation % — top 15 highest (sorted)" height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={perVehicle.slice(0,15).map(v=>({name:v.reg||'—',dep:v.depPct}))}
                                layout="vertical" margin={{top:0,right:50,left:80,bottom:0}}>
                                <XAxis type="number" tick={{fontSize:10}} domain={[0,100]} tickFormatter={v=>`${v}%`}/>
                                <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={75}/>
                                <Tooltip formatter={v=>[`${v.toFixed(1)}%`,'Depreciated']}/>
                                <Bar dataKey="dep" radius={[0,4,4,0]}>
                                    {perVehicle.slice(0,15).map((v,i)=>
                                        <Cell key={i} fill={depColor(v.depPct)}/>
                                    )}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Fleet Age Profile" height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ageBuckets} margin={{top:10,right:10,left:0,bottom:10}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="name" tick={{fontSize:10}}/>
                                <YAxis tick={{fontSize:10}} allowDecimals={false}/>
                                <Tooltip/>
                                <Bar dataKey="count" name="Vehicles" radius={[4,4,0,0]}>
                                    {ageBuckets.map((_,i)=>
                                        <Cell key={i} fill={['#52c41a','#faad14','#fa8c16','#f5222d'][i]}/>
                                    )}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {!loading && filtered.length === 0 && (
                    <div style={{textAlign:'center',padding:60,color:'#aaa',
                        border:'2px dashed #e8e8e8',borderRadius:8}}>
                        <div style={{fontSize:32,marginBottom:12}}>📉</div>
                        <div style={{fontWeight:600,marginBottom:4}}>No depreciation data</div>
                        <div style={{fontSize:12}}>Set procurement cost and installation date on vehicles.</div>
                    </div>
                )}
            </Spin>
        </div>
    );
}